import { useEffect, useState } from 'react';
import { toast } from '@/lib/toast';
import { useBulkUpdateProductFields } from '@/hooks/useProducts';
import { useBulkSetInventory, useLocations } from '@/hooks/useVariants';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FilterChips } from '@/components/ui/filter-chips';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { BulkTargets } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targets: BulkTargets;
  onApplied: () => void;
}

type Mode = 'status' | 'stock';

const MODE_CHIPS = [
  { value: 'status', label: 'Product status' },
  { value: 'stock', label: 'Stock level' },
];

const STATUS_CHIPS = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];

/**
 * Bulk-edit availability — a product's status (active / draft / archived) or
 * the on-hand stock count for the selected variants at a chosen location.
 * Status is a product-level edit; stock is a per-variant inventory push.
 */
export function BulkStatusInventoryDialog({ open, onOpenChange, targets, onApplied }: Props) {
  const [mode, setMode] = useState<Mode>('status');
  const [status, setStatus] = useState('active');
  const [qty, setQty] = useState('');
  const [locationId, setLocationId] = useState<string>('');

  const { data: locations = [] } = useLocations();
  const bulkProduct = useBulkUpdateProductFields();
  const bulkInventory = useBulkSetInventory();
  const pending = bulkProduct.isPending || bulkInventory.isPending;

  // Default to the primary location once locations load.
  useEffect(() => {
    if (locationId === '' && locations.length > 0) {
      setLocationId(String(locations[0].shopify_location_id));
    }
  }, [locations, locationId]);

  const handleApplyStatus = async () => {
    try {
      await bulkProduct.mutateAsync(
        targets.productShopifyIds.map((id) => ({
          shopifyProductId: id,
          fields: { status },
        })),
      );
      toast.success(
        `Set ${targets.productShopifyIds.length} products to "${status}" in Shopify`,
      );
      onOpenChange(false);
      onApplied();
    } catch (err) {
      toast.error(`Bulk status update failed: ${(err as Error).message}`);
    }
  };

  const handleApplyStock = async () => {
    const available = parseInt(qty, 10);
    if (isNaN(available) || available < 0) {
      toast.error('Enter a stock quantity of 0 or more');
      return;
    }
    if (locationId === '') {
      toast.error('Pick a location');
      return;
    }
    const variants = await targets.resolveVariants().catch((err) => {
      toast.error(`Could not load variants: ${(err as Error).message}`);
      return null;
    });
    if (!variants) return;
    if (variants.length === 0) {
      toast.error('No variants in the selection');
      return;
    }
    try {
      await bulkInventory.mutateAsync({
        items: variants.map((v) => ({
          shopify_inventory_item_id: v.shopify_inventory_item_id,
          shopify_variant_id: v.shopify_variant_id,
        })),
        locationId: Number(locationId),
        available,
      });
      toast.success(`Set stock to ${available} on ${variants.length} variants in Shopify`);
      setQty('');
      onOpenChange(false);
      onApplied();
    } catch (err) {
      toast.error(`Bulk stock update failed: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Inventory &amp; status</DialogTitle>
          <DialogDescription>Applies to {targets.selectionLabel}.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <FilterChips options={MODE_CHIPS} value={mode} onChange={(v) => setMode(v as Mode)} />

          {mode === 'status' ? (
            <div>
              <label className="mb-2 block text-xs font-medium text-muted-foreground">
                Set product status to
              </label>
              <FilterChips options={STATUS_CHIPS} value={status} onChange={setStatus} />
              <p className="mt-2 text-[11px] text-muted-foreground">
                Status is a product-level setting — it applies to{' '}
                {targets.productShopifyIds.length}{' '}
                {targets.productShopifyIds.length === 1 ? 'product' : 'products'}.
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Location
                </label>
                {locations.length <= 1 ? (
                  <p className="text-sm text-foreground">
                    {locations[0]?.name ?? 'Loading…'}
                  </p>
                ) : (
                  <Select value={locationId} onValueChange={setLocationId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Pick a location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => (
                        <SelectItem
                          key={l.shopify_location_id}
                          value={String(l.shopify_location_id)}
                        >
                          {l.name ?? `Location ${l.shopify_location_id}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                  Stock quantity
                </label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  placeholder="0"
                  autoFocus
                />
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  Sets the same on-hand count on every selected variant at this location.
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            className="w-full sm:w-auto"
            onClick={mode === 'status' ? handleApplyStatus : handleApplyStock}
            disabled={pending}
          >
            {pending ? 'Applying…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
